import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Modal, InputNumber, Input, Skeleton, Button, message } from '../../ui';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { Pill } from '../../components/TwDataTable';
import { inr2 } from '../../lib/format';
// One definition of what a reason is CALLED and what colour it wears, shared with the return record — a
// reason that changed wording between the record and the screen that pays it would read as a different fact.
import { REASON_META } from './ReturnDetailPage';

/**
 * ACCOUNTS CREDIT — Phase 3 of the return lifecycle. Once the WHM has RECEIVED the goods, Accounts issues the
 * credit note(s). Per line the credit qty is pre-filled from the reason (seller/carrier fault → full, buyer
 * choice → 0) and capped at what was received; Accounts edits as needed. On submit the credited qty is allocated
 * across the order's invoices (oldest first) and one GST credit note is posted per invoice → the retailer's
 * advance-in-credit (no cash refund). The return moves RECEIVED → CREDITED.
 */

export function CreditReturnModal({ open, onClose, returnId }: { open: boolean; onClose: () => void; returnId: string }) {
  const qc = useQueryClient();
  const [credits, setCredits] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');

  const { data: preview, isLoading } = useQuery<any>({
    queryKey: ['credit-preview', String(returnId)],
    queryFn: () => request({ url: `/returns/${returnId}/credit-preview` }),
    enabled: open && !!returnId,
  });

  // Seed each line's credit to the reason-driven suggestion when the preview arrives.
  useEffect(() => {
    if (!open || !preview) return;
    const seed: Record<string, number> = {};
    for (const l of preview.lines ?? []) seed[String(l.returnLineId)] = Number(l.suggestedCredit ?? 0);
    setCredits(seed);
    setNote('');
  }, [open, preview]);

  const lines = preview?.lines ?? [];
  const lineCredit = (l: any) => Number(credits[String(l.returnLineId)] ?? 0);
  const overCap = lines.some((l: any) => lineCredit(l) > Number(l.receivedQty ?? 0) + 1e-6);
  const totalCredit = lines.reduce((s: number, l: any) => s + lineCredit(l) * Number(l.unitGross ?? 0), 0);

  const send = useConfirmedMutation({ title: "Issue a credit note for this return?", content: "The credit posts to the retailer’s ledger. Reversing it later needs a debit note.", okText: "Yes, issue it" }, {
    mutationFn: () => request({
      url: `/returns/${returnId}/credit`, method: 'POST',
      data: {
        note: note.trim() || undefined,
        lines: lines.map((l: any) => ({ returnLineId: String(l.returnLineId), creditQty: Number(lineCredit(l)) })),
      },
    }),
    onSuccess: (r: any) => {
      message.success(totalCredit > 0 ? `Return ${r?.returnNo ?? ''} credited — advance updated` : `Return ${r?.returnNo ?? ''} closed`);
      qc.invalidateQueries({ queryKey: ['return', String(returnId)] });
      qc.invalidateQueries({ queryKey: ['returns'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      qc.invalidateQueries({ queryKey: ['retailer'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Credit failed'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Issue credit note"
      width={720}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={send.isPending} disabled={overCap || isLoading} onClick={() => send.mutate()}>
            {totalCredit > 0 ? `Issue credit — ${inr2(totalCredit)}` : 'Close with no credit'}
          </Button>
        </>
      }
    >
      {isLoading || !preview ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        <>
          <div className="mb-4 rounded-control border border-sky-500/25 bg-sky-500/10 px-3.5 py-2.5 text-sm leading-relaxed text-content">
            The credit is pre-filled from the return reason and capped at what the warehouse received — edit any line.
            <span className="text-muted"> It posts one GST credit note per invoice → the retailer’s advance (credit) balance. No cash refund.</span>
          </div>

          {/*
            * ONE FAULT LINE CANNOT SPEAK FOR A MIXED RETURN.
            *
            * This said "Damaged in transit · seller/carrier fault · full credit suggested" for the whole
            * document, which is the person paying being told the wrong thing on any return whose products
            * came back for different reasons: the table underneath correctly suggests a full credit on the
            * damaged product and nothing on the over-ordered one, while the headline claims the whole return
            * is the seller's fault. When the reasons vary, the reasons are named per product instead.
            */}
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted">Reason:</span>
            {preview.reasonsVary ? (
              <>
                <span className="font-medium text-content">varies by product</span>
                <Pill color="gold">each line suggested on its own fault</Pill>
              </>
            ) : (
              <>
                <span className="font-medium text-content">{REASON_META[String(preview.reasonCode ?? '')]?.label ?? '—'}</span>
                {preview.sellerFault
                  ? <Pill color="green">seller / carrier fault · full credit suggested</Pill>
                  : <Pill color="gold">buyer choice · credit at your discretion</Pill>}
              </>
            )}
            {preview.invoiceCount > 1 && <span className="text-xs text-subtle">spans {preview.invoiceCount} invoices — split oldest-first</span>}
          </div>

          <div className="overflow-hidden rounded-control border border-line">
            <table className="w-full border-collapse text-sm">
              <thead className="gb-thead-tint bg-surface-2">
                <tr>
                  <th className="px-3 py-2.5 text-left type-overline text-subtle">Product</th>
                  {/* Only when the products disagree — on a single-reason return the line above already
                      said it once, and repeating it down every row tells the reader nothing new. */}
                  {preview.reasonsVary && <th className="px-3 py-2.5 text-left type-overline text-subtle">Why</th>}
                  <th className="px-3 py-2.5 text-right type-overline text-subtle">Received</th>
                  <th className="px-3 py-2.5 text-right type-overline text-subtle">Credit qty</th>
                  <th className="px-3 py-2.5 text-right type-overline text-subtle">Credit value</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l: any) => {
                  const received = Number(l.receivedQty ?? 0);
                  const cq = lineCredit(l);
                  return (
                    <tr key={l.returnLineId} className="border-t border-line">
                      <td className="px-3 py-2.5 align-middle font-medium text-content">{l.productName}</td>
                      {preview.reasonsVary && (
                        <td className="px-3 py-2.5 align-middle">
                          <div className="text-content">{REASON_META[String(l.reasonCode ?? '')]?.label ?? '—'}</div>
                          {/* The fault is what decides the suggestion in the next column, so it is said
                              beside it rather than left for the reader to infer from the number. */}
                          <div className="type-caption text-muted">{l.sellerFault ? 'seller fault' : 'buyer choice'}</div>
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-right align-middle tabular-nums text-content">{fmtQty(received)}</td>
                      <td className="px-3 py-2.5 text-right align-middle">
                        <InputNumber
                          min={0}
                          max={received}
                          step={1}
                          value={cq}
                          status={cq > received ? 'error' : undefined}
                          onChange={(v) => setCredits((m) => ({ ...m, [String(l.returnLineId)]: Number(v ?? 0) }))}
                          style={{ width: 96 }}
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right align-middle font-semibold text-content tabular-nums">{inr2(cq * Number(l.unitGross ?? 0))}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-line-strong bg-surface-2">
                  {/* Spans every column left of the value — one more when the per-product "Why" is shown,
                      or the total slides out from under its own figure. */}
                  <td className="px-3 py-2.5 type-body-strong text-content" colSpan={preview.reasonsVary ? 4 : 3}>Total credit (incl. GST)</td>
                  <td className="px-3 py-2.5 text-right type-section-title text-brand-700 dark:text-brand-400 tabular-nums">{inr2(totalCredit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-4">
            <div className="mb-1.5 type-label text-muted">Note (optional)</div>
            <Input.TextArea rows={2} maxLength={255} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / reference for this credit (appears on the credit note)" />
          </div>
        </>
      )}
    </Modal>
  );
}
